import { redirect } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';

import { hasLiveInvestorSession } from '../services/investor-session.service';

import { readCachedResolution } from './read-cached-resolution';

import { getHandoffPayload } from '@/features/investor-entry/public';
import { logger } from '@/lib/logger';

/**
 * Order matters: a live session is past this point; no payload means they never came through a
 * link; a missing or non-login resolution belongs to boot. This guard must never resolve itself
 * — an onboard resolve mints a payment session.
 */
export const requireLoginBranch = (queryClient: QueryClient) => {
  if (hasLiveInvestorSession()) throw redirect({ to: '/home', replace: true });

  if (!getHandoffPayload()) {
    logger.error('investor login blocked', { reason: 'link_missing' });
    throw redirect({ to: '/error', search: { reason: 'link_missing' }, replace: true });
  }

  const resolution = readCachedResolution(queryClient);
  if (resolution?.action !== 'login') {
    logger.debug('investor login guard sent the visitor back to boot', { resolvedAction: resolution?.action ?? 'none' });
    throw redirect({ to: '/', replace: true });
  }
};

/** An expired session goes to `/login`, not `/error`: the payload survives the tab, so a fresh
 * code needs no new link. */
export const requireInvestorSession = () => {
  if (hasLiveInvestorSession()) return;

  if (getHandoffPayload()) {
    logger.info('investor home blocked, sending to sign-in', { reason: 'no_live_session' });
    throw redirect({ to: '/login', replace: true });
  }

  logger.error('investor home blocked', { reason: 'link_missing' });
  throw redirect({ to: '/error', search: { reason: 'link_missing' }, replace: true });
};
